import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { MessageCircle, Send, User, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import ReportBlockMenu from './ReportBlockMenu';

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

// AUDIT 2026-06 (P1/P2, Opus+Gemini high-conf): dedupe-by-id + stable sort.
// `before=` keyset cursors can return rows that overlap on equal timestamps,
// so a naive [...older, ...prev] produced duplicate React keys (key warnings +
// mis-render). Mirror CommunityChat's merge-by-id and keep ascending order.
function mergeById(a, b) {
  const map = new Map();
  for (const c of (a || [])) map.set(c.id, c);
  for (const c of (b || [])) map.set(c.id, c);
  return [...map.values()].sort(
    (x, y) => new Date(x.created_at || 0) - new Date(y.created_at || 0)
  );
}

export default function EntityComments({ entityId, entityName }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const res = await api.get(`/entities/${entityId}/comments`, { params: { limit: 50 } });
      setComments(res.data.comments || []);
      setTotal(res.data.total || 0);
      setHasMore(res.data.has_more || false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const loadOlder = async () => {
    if (!comments.length) return;
    setLoadingMore(true);
    try {
      const oldest = comments[0]?.created_at;
      const res = await api.get(`/entities/${entityId}/comments`, { params: { limit: 50, before: oldest } });
      const older = res.data.comments || [];
      setComments(prev => mergeById(older, prev));
      setHasMore(res.data.has_more || false);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  const sendComment = async () => {
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/entities/${entityId}/comments`, { entity_id: entityId, content: newComment.trim() });
      setNewComment('');
      if (res.data.warning) toast.info(res.data.warning);
      // AUDIT 2026-06 (P2): merge the latest window into existing state instead
      // of a full overwrite, so a user who loaded older history keeps it.
      const latest = await api.get(`/entities/${entityId}/comments`, { params: { limit: 50 } });
      setComments(prev => mergeById(prev, latest.data.comments || []));
      setTotal(latest.data.total || 0);
      setHasMore(prev => latest.data.has_more ?? prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const reportComment = async (commentId, reason) => {
    try {
      const res = await api.post(`/entities/${entityId}/comments/${commentId}/report`, { reason });
      const ref = res.data?.reference;
      toast.success(`Report received${ref ? ` · ref ${ref}` : ''}. Our team will review it within 24h.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit report.');
    }
  };

  const blockAuthor = async (c) => {
    try {
      await api.post('/community/block', { target_user_id: c.user_id });
      setComments((prev) => prev.filter((x) => x.user_id !== c.user_id));
      toast.success(`Blocked ${c.username || 'user'}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not block user.');
    }
  };

  return (
    <div className="bg-card border border-border/60 rounded-2xl overflow-hidden" data-testid="entity-comments">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Discussion</h3>
        </div>
        <Badge variant="secondary" className="text-[10px]">{total} comments</Badge>
      </div>

      <ScrollArea className="h-[240px] px-4 py-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : (
          <div className="space-y-3">
            {hasMore && (
              <button
                className="w-full text-xs text-primary flex items-center justify-center gap-1 py-2 hover:underline"
                onClick={loadOlder}
                disabled={loadingMore}
              >
                <ChevronUp className="h-3 w-3" />
                {loadingMore ? 'Loading...' : 'Load older comments'}
              </button>
            )}
            {comments.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No comments yet. Be the first to discuss {entityName}!
              </div>
            )}
            {comments.map((c) => (
              <div key={c.id} className="flex gap-2 group" data-testid={`comment-${c.id}`}>
                <div className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center text-[10px] font-bold shrink-0">
                  {c.username?.charAt(0) || <User className="h-3 w-3" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold">{c.username}</span>
                    <span className="text-[9px] text-muted-foreground">{formatTimestamp(c.created_at)}</span>
                    {user && c.user_id && c.user_id !== user.id && (
                      <div className="ml-auto opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <ReportBlockMenu
                          targetUserId={c.user_id}
                          targetUsername={c.username || 'this user'}
                          contentLabel="comment"
                          onReport={(reason) => reportComment(c.id, reason)}
                          onBlock={() => blockAuthor(c)}
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-foreground/80 mt-0.5">{c.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="px-4 py-3 border-t border-border/40">
        <div className="flex gap-2">
          <Input
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendComment()}
            className="flex-1 h-8 text-xs"
            maxLength={300}
            data-testid="entity-comment-input"
          />
          <Button size="sm" className="h-8" onClick={sendComment} disabled={sending || !newComment.trim()} data-testid="entity-comment-send">
            <Send className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
